// Dev-only fixtures + dispatcher for the system-prompt cascade (#635/#637) —
// the ORG layer (`/v1/system-prompts/org`), the per-repo layer
// (`/v1/system-prompts/repo/:repoId`) and the acting user's own
// (`/v1/me/system-prompt`). Same null-or-row envelope on all three.
//
// Split out of `mocks.ts` (plan 101 slice 15 step 4). `null` means "not mine"
// and `mockFetch` falls through.
//
// STATEFUL, and it had to become so here: `usePromptLayer` awaits
// `query.refetch()` in the `finally` of every save AND every clear, so the
// stateless version served the seed text back on the next GET — a save that
// reads as silently discarded and a Clear that reads as a dead button, on all
// three layers at once. That is the rule `mocks.test.ts` already pins for
// llm-routes, invitations and tokens.
//
// No `Resource` exists for a prompt layer — `apps/api/src/routes/system-
// prompts.ts` projects it inline — so `SystemPrompt` (the dashboard's own type)
// is what grades these.

import { at } from './mock-session';
import type { PromptScope, SystemPrompt } from './system-prompts';

// Every layer is keyed by `(scope, repoId, role)` — the API's own address since
// `?role=` (plan 101 slice 06): a reviewer-only prose row is a DIFFERENT row
// from the all-roles one, and a mock that ignored the param would let a role
// save overwrite the every-run prose in a story.
type LayerKey = string;
const keyOf = (scope: PromptScope, repoId: string, role: string): LayerKey =>
  `${scope}|${repoId}|${role}`;

/** The pristine layers. Absent is "this layer is unauthored and INHERITS". */
const SEED: readonly (readonly [LayerKey, SystemPrompt])[] = [
  [
    keyOf('org', '', ''),
    {
      scope: 'org',
      scope_ref: null,
      text: 'You are the maintainer agent for developerz-ai. Triage issues, keep a calm operational tone, hand coding to the reporter’s own agent, and always disclose that you are a bot.',
      updated_at_iso: at('2026-06-15'),
    },
  ],
  // The repo and user layers, and every role layer, start unauthored, so the
  // console shows what an INHERITING layer looks like — the common case, and
  // the one a seeded fixture everywhere would have hidden.
];
const layers = new Map<LayerKey, SystemPrompt>();

/** Which layer an address names, or `null` when it names none of them. */
function layerFor(bare: string): { scope: PromptScope; repoId: string } | null {
  if (bare === '/v1/system-prompts/org') return { scope: 'org', repoId: '' };
  if (bare === '/v1/me/system-prompt') return { scope: 'user', repoId: '' };
  if (bare.startsWith('/v1/system-prompts/repo/')) {
    return {
      scope: 'repo',
      repoId: decodeURIComponent(bare.slice('/v1/system-prompts/repo/'.length)),
    };
  }
  return null;
}

/** Serve the three prompt layers, per role; `null` for anything else. */
export function systemPromptsMock(
  path: string,
  method: string,
  init?: RequestInit,
): Promise<unknown> | null {
  const [bare = path, query = ''] = path.split('?');
  const layer = layerFor(bare);
  if (layer === null) return null;
  const key = keyOf(layer.scope, layer.repoId, new URLSearchParams(query).get('role') ?? '');
  if (method === 'GET') return Promise.resolve({ prompt: layers.get(key) ?? null });
  if (method === 'PUT') {
    const body = init?.body ? (JSON.parse(String(init.body)) as { text?: string }) : {};
    const row: SystemPrompt = {
      scope: layer.scope,
      scope_ref: layer.scope === 'repo' ? layer.repoId : null,
      text: body.text ?? '',
      updated_at_iso: at('2026-07-24'),
    };
    layers.set(key, row);
    return Promise.resolve({ prompt: row });
  }
  if (method === 'DELETE') {
    layers.delete(key);
    // 204: the route answers no body, and `clearOrgPrompt` types it `void`.
    return Promise.resolve(undefined);
  }
  return null;
}

/** Restore the pristine fixtures — the shared reset seam (`./mock-state.ts`). */
export function resetSystemPromptsMocks(): void {
  layers.clear();
  for (const [key, value] of SEED) layers.set(key, structuredClone(value));
}
resetSystemPromptsMocks();
